import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { replayChart } from '@/app/lib/quant/chartReplay'
import { quantApi } from '@/app/lib/quant/quantApi'
import { isQuantAbort, quantErrorCode, quantErrorMessage } from '@/app/lib/quant/quantErrors'
import { readChartBars, writeChartBars } from '@/app/lib/quant/quantChartBars'
import type { ChartModel } from '@/app/lib/quant/types'

/** not_ready: written when the run finishes; missing: no chart exists; failed: broken, retryable. */
export type ChartErrorKind = 'not_ready' | 'missing' | 'failed'

interface ChartFailure {
  message: string
  code: string | null
  kind: ChartErrorKind
}

function failureOf(error: unknown): ChartFailure {
  const code = quantErrorCode(error)
  const kind: ChartErrorKind = code === 'chart_not_ready' ? 'not_ready' : code === 'chart_missing' ? 'missing' : 'failed'
  return { message: quantErrorMessage(error), code, kind }
}

export type QuantRunChartState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; model: ChartModel }
  | { status: 'error'; message: string; code: string | null; kind: ChartErrorKind }

export type QuantRunChart = QuantRunChartState & {
  retry: () => void
  bars: number
  setBars: (bars: number) => void
  /** Point count of the first candlestick series once ready, else null. */
  loadedCount: number | null
}

/**
 * A run's chart.json, replayed once into a `ChartModel`. Lazy: nothing is
 * requested until `enabled` first becomes true (the Chart tab was opened).
 * The bar count is a per-strategy preference; changing it refetches.
 */
export function useQuantRunChart(
  runId: string,
  strategySlug: string,
  enabled: boolean,
  onNotReady?: () => void,
): QuantRunChart {
  const [picked, setPicked] = useState<{ slug: string; bars: number } | null>(null)
  const bars = picked !== null && picked.slug === strategySlug ? picked.bars : readChartBars(strategySlug)
  const [raw, setRaw] = useState<{ key: string; result: ChartModel | ChartFailure } | null>(null)
  const [nonce, setNonce] = useState(0)
  const key = `${runId}:${bars}:${nonce}`
  const onNotReadyRef = useRef(onNotReady)
  const notReadyKeyRef = useRef<string | null>(null)
  useEffect(() => {
    onNotReadyRef.current = onNotReady
  })

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    const controller = new AbortController()
    quantApi
      .getRunChart(runId, bars, controller.signal)
      .then((messages) => {
        if (!cancelled) setRaw({ key, result: replayChart(messages) })
      })
      .catch((error: unknown) => {
        if (cancelled || isQuantAbort(error)) return
        const failure = failureOf(error)
        setRaw({ key, result: failure })
        if (failure.kind === 'not_ready' && notReadyKeyRef.current !== key) {
          notReadyKeyRef.current = key
          onNotReadyRef.current?.()
        }
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [runId, bars, enabled, key])

  const retry = useCallback(() => setNonce((n) => n + 1), [])
  const setBars = useCallback(
    (next: number) => {
      writeChartBars(strategySlug, next)
      setPicked({ slug: strategySlug, bars: next })
    },
    [strategySlug],
  )
  return useMemo((): QuantRunChart => {
    const extra = { retry, bars, setBars, loadedCount: null }
    if (!enabled) return { status: 'idle', ...extra }
    if (raw === null || raw.key !== key) return { status: 'loading', ...extra }
    if ('kind' in raw.result) return { status: 'error', ...raw.result, ...extra }
    const candles = raw.result.series.find((s) => s.type === 'candlestick')
    return { status: 'ready', model: raw.result, ...extra, loadedCount: candles?.points.length ?? 0 }
  }, [enabled, raw, key, retry, bars, setBars])
}
